import React, { useState, useEffect } from 'react'
import {
  Modal,
  Form,
  DatePicker,
  Select,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
} from 'antd'
import moment from 'moment'
import MultiCauseCodePropmt from '../../card-data/MultiCauseCode'

const { Option } = Select
const { TextArea } = Input

const AddEventModal = ({
  visible,
  onCancel,
  onSave,
  form,
  initialDate,
  initialHour,
  selectedUnit,
  editData,
  eventTypes,
  contribConditions,
  causeCodeList,
  unallocatedMW,
}) => {
  const { getFieldDecorator } = form

  // Set initial time based on passed prop
  const defaultTime = initialDate
    ? moment(initialDate)
        .hour(parseInt(initialHour) || 0)
        .minute(0)
    : moment()

  // Mock Data for Dropdowns - Removed Mock Event Types
  // const eventTypes = ['U1', 'U2', 'U3', 'D1', 'D2', 'D3', 'SF', 'RS']
  // Mock Data for Dropdowns - Removed Mock Event Types
  // const eventTypes = ['U1', 'U2', 'U3', 'D1', 'D2', 'D3', 'SF', 'RS']
  // const contribConditions removed in favor of conCodes from utils

  const [causeCodeSearchVisible, setCauseCodeSearchVisible] = useState(false)
  const [selectedCauseCode, setSelectedCauseCode] = useState(null)

  // State for MultiCauseCodePropmt
  const [causeCodeKeyValues, setCauseCodeKeyValues] = useState(null)
  const [causeCodeValues, setCauseCodeValues] = useState(null)

  // Use ref to hold form instance to avoid dependency triggering
  const formRef = React.useRef(form)

  useEffect(() => {
    formRef.current = form
  }, [form])

  useEffect(() => {
    // Access current form instance
    const f = formRef.current

    if (visible && editData) {
      console.log('Edit Data:', editData)
      // Edit Mode
      // Ensure startTime is a moment object

      f.setFieldsValue({
        startTime: moment(editData.startTime),
        eventType: editData.eventType,
        comments: editData.comments,
        causeCode: editData.causeCode,
        contribCondition: editData.contribCondition,
        mwAmount: editData.mwAmount,
      })

      // Try to set selected cause code for display if available
      if (editData.causeCode) {
        setSelectedCauseCode({
          causeCode: editData.causeCode,
          description: editData.causeCodeDescription || 'Existing',
        })
      }
    }
  }, [visible, editData]) // Removed form from deps

  const handleSave = () => {
    form.validateFields((err, values) => {
      if (!err) {
        onSave({
          ...values,
          causeCode: selectedCauseCode
            ? selectedCauseCode.causeCode
            : values.causeCode,
          causeCodeDescription: selectedCauseCode
            ? selectedCauseCode.description
            : '',
        })
        form.resetFields()
        setSelectedCauseCode(null)
        setCauseCodeKeyValues(null)
        setCauseCodeValues(null)
      }
    })
  }

  // Handle MultiCauseCode close/select
  const onCauseCodeDialogClose = (keys, values) => {
    setCauseCodeSearchVisible(false)
    setCauseCodeKeyValues(keys)
    setCauseCodeValues(values)

    if (values && values.length > 0) {
      const cc = values[0]
      setSelectedCauseCode(cc)
      form.setFieldsValue({ causeCode: `${cc.causeCode} - ${cc.description}` })
    }
  }

  const onCauseCodeTreeCancel = keys => {
    setCauseCodeSearchVisible(false)
    setCauseCodeKeyValues(keys)
  }

  return (
    <Modal
      visible={visible}
      title="Add Event"
      onCancel={onCancel}
      onOk={handleSave}
      width={900}
      maskClosable={false}
      zIndex={101}
    >
      <Form layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Start Time">
              {getFieldDecorator('startTime', {
                initialValue: defaultTime,
                rules: [
                  { required: true, message: 'Please select start time!' },
                ],
              })(
                <DatePicker
                  showTime
                  format="MM/DD/YYYY HH:mm"
                  style={{ width: '100%' }}
                />
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Event Type">
              {getFieldDecorator('eventType', {
                rules: [
                  { required: true, message: 'Please select event type!' },
                ],
              })(
                <Select placeholder="Select Event Type" showSearch>
                  {(eventTypes || []).map(e => (
                    <Option key={e.eventType} value={e.eventType}>
                      {e.eventType} - {e.description}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={
            <span>
              {`Derate Amount (MW) for Hour ${initialHour || ''}`}
              {unallocatedMW !== undefined && (
                <span
                  style={{
                    marginLeft: 10,
                    color: '#888',
                    fontWeight: 'normal',
                  }}
                >
                  (Unallocated: {unallocatedMW})
                </span>
              )}
            </span>
          }
        >
          {getFieldDecorator('mwAmount', {
            initialValue: unallocatedMW, // Default to unallocated amount if available? Or just empty. User request: "so they know what to put in". Let's default to it.
            rules: [{ required: true, message: 'Please enter MW amount!' }],
          })(<InputNumber style={{ width: '100%' }} />)}
        </Form.Item>

        <Form.Item label="Comments">
          {getFieldDecorator('comments', {
            rules: [{ required: true, message: 'Please enter comments!' }],
          })(<TextArea rows={4} />)}
        </Form.Item>

        <Form.Item label="Cause Code">
          <div style={{ display: 'flex', gap: '8px' }}>
            {getFieldDecorator('causeCode', {
              rules: [
                {
                  required: true,
                  message: 'Please select or enter a cause code!',
                },
              ],
            })(<Input placeholder="Enter Cause Code" disabled />)}
            <Button
              type="primary"
              onClick={() => setCauseCodeSearchVisible(true)}
            >
              Select
            </Button>
          </div>
          {selectedCauseCode && (
            <div style={{ marginTop: '5px', color: 'green' }}>
              Selected: {selectedCauseCode.description} (
              {selectedCauseCode.causeCode})
            </div>
          )}
        </Form.Item>

        <Form.Item label="Contributing Operating Condition" required>
          {getFieldDecorator('contribCondition', {
            rules: [{ required: true, message: 'Please select a condition!' }],
          })(
            <Select placeholder="Select Condition">
              {(contribConditions || []).map(item => (
                <Option key={item.code} value={item.code}>
                  {item.code} - {item.description}
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>
      </Form>

      {/* Real Cause Code Selector */}
      {causeCodeSearchVisible && (
        <MultiCauseCodePropmt
          visibleDialog={causeCodeSearchVisible}
          onDialogClose={onCauseCodeDialogClose}
          onCancelAction={onCauseCodeTreeCancel}
          selectedKeys={causeCodeKeyValues}
          selectedValues={causeCodeValues}
          causeCodeList={causeCodeList}
          unitCodes={selectedUnit ? [selectedUnit] : []}
          multiple={false}
          zIndex={1200}
          selectable
          causeCodeValue={
            selectedCauseCode ? [selectedCauseCode.causeCode] : []
          }
        />
      )}
    </Modal>
  )
}

export default Form.create()(AddEventModal)
